import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { withApollo } from 'react-apollo';
import { Switch } from 'antd';
import { gql } from 'graphql-tag';
import NominationRewardsListContainer from '../NominationRewardsListContainer';
import { RewardList as RewardListContainer } from '../../../RnRCommonComponent';
import './style.scss';
import {
	BADGE_TYPE,
	REVERSE_MAPING_BADGE_TYPE,
	ICON_COMPLETE_MAP,
} from '../../../RnRCommonComponent/CoreBadge/rewardsConstants.js';
import useGetNominatedConfigList from 'src/nominatedRewards/hooks/useGetNominatedConfigList.js';

const AllRewardsListContainer = ({ client }) => {
	const currentUser = useSelector((state) => state.user.currentUser);
	const catalogOnlyGroupedByCategory =
		currentUser?.company?.recognitionSettings?.catalogOnlyGroupedByCategory;

	const { openNominationsList, completeNominationsList, winningNominations } =
		useGetNominatedConfigList({ client, currentUser });

	const [shouldRefresh, setShouldRefresh] = useState(false);
	const [groupingMode, setGroupingMode] = useState(
		catalogOnlyGroupedByCategory ? 'category' : 'badgeType'
	); // 'badgeType' or 'category'
	const [categoryBadges, setCategoryBadges] = useState({});
	const [isLoading, setIsLoading] = useState(false);

	const QUERY_BADGES_BY_COMPANY_ID = gql`
		query QueryBadgesByCompanyIdIndex(
			$companyId: ID!
			$limit: Int
			$nextToken: String
		) {
			queryBadgesByCompanyIdIndex(
				companyId: $companyId
				limit: $limit
				nextToken: $nextToken
			) {
				items {
					id
					allowSelfNomination
					companyId
					badgeTypeId
					iconId
					name
					label
					description
					color
					gradientColor
					points
					shape
					badgeRotation
					customImageKey
					customImageSignedUrl
					labelColor
					customIconKey
					customIconSignedUrl
					category
					visibility
				}
				nextToken
			}
		}
	`;

	const processBadgesByCategory = (badgeItems) => {
		const enrichedBadges = badgeItems.map((badge) => {
			const badgeType = REVERSE_MAPING_BADGE_TYPE[badge.badgeTypeId] || '';

			let iconInfo = null;
			if (badge.iconId) {
				iconInfo = ICON_COMPLETE_MAP.find((icon) => icon.id === badge.iconId);
			}

			return {
				...badge,
				badgeType,
				iconInfo,
			};
		});

		const groupedByCategory = enrichedBadges.reduce((acc, badge) => {
			const category = badge.category || 'Default';
			if (!acc[category]) {
				acc[category] = [];
			}
			acc[category].push(badge);
			return acc;
		}, {});

		setCategoryBadges(groupedByCategory);
	};

	const fetchBadgesByCompany = async () => {
		if (client && currentUser?.companyId) {
			try {
				setIsLoading(true);

				const { data, error } = await client.query({
					query: QUERY_BADGES_BY_COMPANY_ID,
					variables: {
						companyId: currentUser.companyId,
						limit: 5000,
						nextToken: null,
					},
					fetchPolicy: 'network-only',
				});

				if (error) {
					console.error('Error fetching badges by company ID:', error);
					return;
				}

				if (data?.queryBadgesByCompanyIdIndex?.items) {
					processBadgesByCategory(data.queryBadgesByCompanyIdIndex.items);
				}
			} catch (error) {
				console.error('Exception when fetching badges by company ID:', error);
			} finally {
				setIsLoading(false);
			}
		}
	};

	useEffect(() => {
		if (groupingMode === 'category') {
			fetchBadgesByCompany();
		}
	}, [groupingMode, client, currentUser]);

	const nominatedRewards = [
		{
			title: 'Open Nominations',
			openNomination: true,
			badgeList: openNominationsList,
			emptyString: 'No Nominations found.',
		},
		{
			title: 'Nomination Winners',
			nominatedAwardWinner: true,
			winningNominations: winningNominations,
			badgeList: completeNominationsList,
			emptyString: 'Winners not declared yet.',
		},
	];

	const allAward = [
		{
			title: 'On-The-Spot Recognitions',
			badgeType: BADGE_TYPE.PEERTOPEER,
		},
		{
			title: 'Milestones',
			badgeType: BADGE_TYPE.AUTOMATED,
		},
		{
			title: 'Nominations',
			badgeType: BADGE_TYPE.NOMINATED,
		},
	];

	return (
		<div className="all-rewards">
			<div className="main">
				{openNominationsList.length > 0 ||
				completeNominationsList.length > 0 ? (
					<>
						<div className="page-title">
							<h2 className="page-heading">Nominations</h2>
						</div>
						<div className="row">
							{nominatedRewards?.map((props, index) => (
								<div className="col-lg-6 reward-winner" key={index}>
									<NominationRewardsListContainer
										{...props}
										currentUser={currentUser}
									/>
								</div>
							))}
						</div>
					</>
				) : null}
				<div className="page-title mt-3">
					<h2 className="page-heading">All Recognitions</h2>
				</div>
				{!catalogOnlyGroupedByCategory && (
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: '12px',
							marginBottom: '20px',
							padding: '12px 16px',
							borderRadius: '8px',
						}}
					>
						<span style={{ fontWeight: '700', color: '#151719ff' }}>
							Group by:
						</span>
						<span
							style={{
								color:
									groupingMode === 'badgeType'
										? 'oklch(0.2998 0.034 260.85)'
										: 'oklch(0.2998 0.034 260.85)',
							}}
						>
							Badge Type
						</span>
						<Switch
							checked={groupingMode === 'category'}
							onChange={(checked) =>
								setGroupingMode(checked ? 'category' : 'badgeType')
							}
							size="default"
							style={
								groupingMode === 'category'
									? { backgroundColor: 'oklch(0.6757 0.187 349.98)' }
									: {}
							}
						/>
						<span
							style={{
								color:
									groupingMode === 'category'
										? 'oklch(0.6757 0.187 349.98)'
										: 'oklch(0.2998 0.034 260.85)',
							}}
						>
							Category
						</span>
					</div>
				)}
				<div className="row">
					<div className="all-award-container col-12">
						{groupingMode === 'badgeType' ? (
							allAward?.map((props, index) => (
								<RewardListContainer
									{...props}
									key={index}
									shouldRefresh={shouldRefresh}
									setShouldRefresh={setShouldRefresh}
									isDetailsShown={false}
									isPointsShown={true}
									isIssuedVisible={false}
									currentUser={currentUser}
									client={client}
								/>
							))
						) : isLoading ? (
							<div className="loading-container">Loading badges...</div>
						) : (
							Object.entries(categoryBadges).map(
								([category, categoryBadgeList]) => (
									<RewardListContainer
										key={category}
										client={client}
										currentUser={currentUser}
										title={category}
										badgeType={null}
										shouldRefresh={false}
										setShouldRefresh={() => {}}
										isDetailsShown={false}
										isPointsShown={true}
										isIssuedVisible={false}
										useCustomData={true}
										badges={categoryBadgeList}
										hasMore={false}
										fetchMoreData={() => {}}
										isLoadingMore={false}
									/>
								)
							)
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default withApollo(AllRewardsListContainer);
